import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { EventNote as EventNoteIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useFechaOperativa } from '../contexts/FechaOperativaContext';
import { formatDateLocal, toLocalDateStringYMD } from '../utils/formatters';

function formatChipDate(ymd: string): string {
  return formatDateLocal(ymd) || ymd;
}

const FechaOperativaChip: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    fechaYmd,
    isOverrideActive,
    setOverride,
    clearOverride,
    openEditorRequestId,
  } = useFechaOperativa();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draftYmd, setDraftYmd] = useState(toLocalDateStringYMD());

  const open = Boolean(anchorEl);

  const openEditor = (el: HTMLElement) => {
    setDraftYmd(fechaYmd ?? toLocalDateStringYMD(new Date()));
    setAnchorEl(el);
  };

  useEffect(() => {
    if (openEditorRequestId <= 0) return;
    const chip = document.getElementById('fecha-operativa-chip');
    if (!chip) return;
    setDraftYmd(fechaYmd ?? toLocalDateStringYMD(new Date()));
    setAnchorEl(chip);
    // Solo reaccionar al pedido desde el menú de usuario.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openEditorRequestId es el disparador
  }, [openEditorRequestId]);

  const handleClose = () => setAnchorEl(null);

  const handleApply = () => {
    if (!draftYmd) return;
    setOverride(draftYmd);
    handleClose();
  };

  const handleUseNow = () => {
    clearOverride();
    handleClose();
  };

  const fechaLabel = isOverrideActive
    ? formatChipDate(fechaYmd!)
    : formatChipDate(toLocalDateStringYMD(new Date()));
  const label = isOverrideActive ? fechaLabel : `Hoy · ${fechaLabel}`;
  const ariaLabel = isOverrideActive
    ? `Fecha operativa ${fechaLabel}`
    : `Fecha de trabajo de hoy ${fechaLabel}`;

  return (
    <>
      <Tooltip
        title={
          isOverrideActive
            ? `Fecha operativa · ${fechaLabel}. Los documentos nuevos usarán esta fecha. Clic para cambiar.`
            : 'Fecha de trabajo. Clic para emitir documentos como en otra fecha (sesión).'
        }
      >
        {isMobile ? (
          <IconButton
            id="fecha-operativa-chip"
            size="small"
            onClick={(e) => openEditor(e.currentTarget)}
            aria-label={ariaLabel}
            sx={{
              flexShrink: 0,
              color: isOverrideActive ? '#ed6c02' : '#00548f',
              bgcolor: isOverrideActive ? alpha('#ed6c02', 0.12) : 'transparent',
              '&:hover': {
                bgcolor: isOverrideActive ? alpha('#ed6c02', 0.2) : 'rgba(0, 84, 143, 0.08)',
              },
            }}
          >
            <EventNoteIcon fontSize="small" />
          </IconButton>
        ) : (
          <Chip
            id="fecha-operativa-chip"
            icon={<EventNoteIcon sx={{ fontSize: '16px !important' }} />}
            label={label}
            size="small"
            onClick={(e) => openEditor(e.currentTarget)}
            sx={{
              fontWeight: 500,
              flexShrink: 0,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              ...(isOverrideActive
                ? {
                    bgcolor: alpha('#ed6c02', 0.12),
                    color: '#b45309',
                    border: '1px solid',
                    borderColor: alpha('#ed6c02', 0.45),
                    '& .MuiChip-icon': { color: '#ed6c02' },
                    '&:hover': { bgcolor: alpha('#ed6c02', 0.2) },
                  }
                : {
                    bgcolor: alpha('#00548f', 0.04),
                    color: '#00548f',
                    border: '1px solid',
                    borderColor: alpha('#00548f', 0.18),
                    '& .MuiChip-icon': { color: '#00548f' },
                    '&:hover': { bgcolor: alpha('#00548f', 0.08) },
                  }),
            }}
          />
        )}
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: isMobile ? 'center' : 'right' }}
        marginThreshold={8}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              width: { xs: 'min(320px, calc(100vw - 24px))', sm: 300 },
              mt: 0.5,
            },
          },
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Fecha operativa
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Aplica a documentos nuevos de esta sesión. No cambia el reloj del
          servidor ni documentos ya guardados.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
            label="Fecha"
            type="date"
            size="small"
            value={draftYmd}
            onChange={(e) => setDraftYmd(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              mt: 0.5,
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              justifyContent: 'flex-end',
            }}
          >
            <Button variant="text" size="small" onClick={handleUseNow} sx={{ textTransform: 'none' }}>
              Usar fecha actual
            </Button>
            <Button variant="contained" size="small" onClick={handleApply} sx={{ textTransform: 'none' }}>
              Aplicar
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default FechaOperativaChip;
